// eslint-disable-next-line import/no-cycle
import { toCamelCaseObject } from '@helpers/transformations';
import {
    useEnabledFeatures as useEnabledFeaturesBase,
    useFeatures as useFeatureBase,
} from '@minds/feature_toggle_react_client';
import userAtom from '@stateAtoms/userAtom';
import { useAtomValue } from 'jotai';
import type { CamelCasedPropertiesDeep } from 'type-fest';

type FeatureOptions = {
    requireCompanyId?: boolean;
};

type FeatureResults<T extends string> = Record<T, boolean>;

type UseFeaturesBaseFn = <T extends string>(
    keys: T[],
    companyId: number,
    options?: FeatureOptions,
) => [FeatureResults<T>, boolean, () => void];

type UseEnabledFeaturesBaseFn = <T extends string>(
    keys: T[],
    companyId: number,
    options?: FeatureOptions,
) => readonly [FeatureResults<T>, boolean, () => void];

export type Keys =
    | 'show_trial_whatsapp'
    | 'oauth_self_provisioning'
    | 'show_menu_phone_report'
    | 'show_authorized_devices'
    | 'enable_certification_create_webhook'
    | 'watchlist_automation'
    | 'show_new_silenced_list'
    | 'voice_cluster_tabs'
    | 'anonymous_phone_antifraud_config'
    | 'show_menu_cases'
    | 'enable_guide_tour'
    | 'show_menu_certified_authentications'
    | 'enable_guide_tour_silenced_list'
    | 'enable_guide_tour_watchlist'
    | 'enable_guide_tour_users'
    | 'enable_guide_tour_watchlist_details'
    | 'request_metadata_filter'
    | 'show_client_history_tab'
    | 'search_grouped_by_external_id' ;

export default function useFeatures<T extends Keys>(keys: T[]) {
    const user = useAtomValue(userAtom);
    return (useFeatureBase as unknown as UseFeaturesBaseFn)(
        keys,
        user?.companyId ?? 0,
    );
}

export function useEnabledFeatures<
    TKeys extends Keys,
>(
    keys: TKeys[],
    options?: Partial<FeatureOptions>,
): [
    Partial<
        CamelCasedPropertiesDeep<FeatureResults<TKeys>>
    >,
    boolean,
    () => void,
] {
    const user = useAtomValue(userAtom);
    const companyId = user?.companyId;

    const effectiveKeys = companyId ? keys : ([] as unknown as TKeys[]);

    const [values, isLoading, refetch] = (
        useEnabledFeaturesBase as unknown as UseEnabledFeaturesBaseFn
    )(effectiveKeys, companyId ?? 0, {
        ...(options ?? {}),
        requireCompanyId: !!companyId,
    });

    const normalizedKeys = toCamelCaseObject(values) as Partial<
        CamelCasedPropertiesDeep<FeatureResults<TKeys>>
    >;

    return [normalizedKeys, isLoading, refetch];
}
